import React, { useState, useEffect } from 'react';
import { Box, Grid, CircularProgress, Typography } from '@mui/material';
import MBFCumulativeFootfallChart from './charts/MBFCumulativeFootfallChart';
import MBFIndividualFootfallChart from './charts/MBFIndividualFootfallChart';
import MBFPeakHoursChart from './charts/MBFPeakHoursChart';
import MBFActiveUsersChart from './charts/MBFActiveUsersChart';
import MBFDepartmentEngagementChart from './charts/MBFDepartmentEngagementChart';
import MBFYearEngagementChart from './charts/MBFYearEngagementChart';

// Import shared components
import { ErrorDisplay, SampleDataBadge, FullscreenChartModal } from '../common';

// Import API
import MBFDashboardAPIs from '../../../utilities/api/dashboard/MBFDashboardAPIs';

// Import sample data utilities
import { isMBFDataEmpty, getMBFSampleData } from '../../../utilities/sampleData/mbfDashboardSampleData';

/**
 * MBF Engagement Tab Component
 * Displays engagement-related charts for MBF Dashboard
 * 
 * @param {object} dateRange - Date range filter { startDate, endDate }
 * @param {string} selectedTL - Selected TL ID for filtering
 */
const MBFEngagementTab = ({ dateRange = {}, selectedTL = '' }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [maximizedChart, setMaximizedChart] = useState(null);

    // Fetch data when dateRange, selectedTL, or refreshTrigger changes
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await MBFDashboardAPIs.getEngagement({
                    startDate: dateRange.startDate,
                    endDate: dateRange.endDate,
                    tlId: selectedTL,
                });
                setData(response);
            } catch (err) {
                console.error('Error fetching MBF engagement data:', err);
                setError('Failed to load engagement data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dateRange.startDate, dateRange.endDate, selectedTL, refreshTrigger]);

    const handleRetry = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    gap: 2
                }}
            >
                <CircularProgress size={48} sx={{ color: '#007bff' }} />
                <Typography sx={{ color: '#666', fontSize: '14px', fontWeight: 500 }}>
                    Fetching engagement data...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return <ErrorDisplay message={error} onRetry={handleRetry} />;
    }

    // Extract data from API response with sample data fallbacks
    const rawCumulativeFootfall = data?.cumulative_footfall || [];
    const isCumulativeEmpty = isMBFDataEmpty(rawCumulativeFootfall, 'array');
    const cumulativeFootfall = isCumulativeEmpty ? getMBFSampleData('cumulativeFootfall') : rawCumulativeFootfall;

    const rawIndividualFootfall = data?.individual_tl_footfall || [];
    const isIndividualEmpty = isMBFDataEmpty(rawIndividualFootfall, 'array');
    const individualTLFootfall = isIndividualEmpty ? getMBFSampleData('individualTLFootfall') : rawIndividualFootfall;

    const rawPeakHours = data?.peak_hours_analysis || [];
    const isPeakHoursEmpty = isMBFDataEmpty(rawPeakHours, 'array');
    const peakHoursAnalysis = isPeakHoursEmpty ? getMBFSampleData('peakHoursAnalysis') : rawPeakHours;

    const rawActiveUsers = data?.active_users_status || {
        active_users: 0,
        inactive_users: 0,
        active_percentage: 0,
        inactive_percentage: 0
    };
    const isActiveUsersEmpty = rawActiveUsers.active_users === 0 && rawActiveUsers.inactive_users === 0;
    const activeUsersStatus = isActiveUsersEmpty ? getMBFSampleData('activeUsersStatus') : rawActiveUsers;

    const rawDeptEngagement = data?.department_wise_engagement || [];
    const isDeptEngagementEmpty = isMBFDataEmpty(rawDeptEngagement, 'array');
    const departmentEngagement = isDeptEngagementEmpty ? getMBFSampleData('departmentEngagement') : rawDeptEngagement;

    const rawYearEngagement = data?.year_wise_engagement || [];
    const isYearEngagementEmpty = isMBFDataEmpty(rawYearEngagement, 'array');
    const yearEngagement = isYearEngagementEmpty ? getMBFSampleData('yearEngagement') : rawYearEngagement;

    // Helper function to get chart title for fullscreen modal
    const getChartTitle = (chartType) => {
        switch (chartType) {
            case 'cumulativeFootfall': return 'Cumulative Footfall Across All TLs';
            case 'individualFootfall': return 'Individual TL Footfall';
            case 'peakHours': return 'Peak Hours Analysis';
            case 'activeUsers': return 'Active Users Status';
            case 'departmentEngagement': return 'Department-wise Engagement';
            case 'yearEngagement': return 'Year-wise Engagement';
            default: return '';
        }
    };

    // Helper function to render chart content in fullscreen modal
    const renderChartContent = (chartType) => {
        switch (chartType) {
            case 'cumulativeFootfall':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MBFCumulativeFootfallChart data={cumulativeFootfall} isSampleData={isCumulativeEmpty} isFullscreen={true} />
                        </Box>
                    </Box>
                );
            case 'individualFootfall':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MBFIndividualFootfallChart data={individualTLFootfall} isSampleData={isIndividualEmpty} isFullscreen={true} />
                        </Box>
                    </Box>
                );
            case 'peakHours':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MBFPeakHoursChart data={peakHoursAnalysis} isSampleData={isPeakHoursEmpty} isFullscreen={true} />
                        </Box>
                    </Box>
                );
            case 'activeUsers':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MBFActiveUsersChart data={activeUsersStatus} isSampleData={isActiveUsersEmpty} isFullscreen={true} />
                        </Box>
                    </Box>
                );
            case 'departmentEngagement':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MBFDepartmentEngagementChart data={departmentEngagement} isSampleData={isDeptEngagementEmpty} isFullscreen={true} />
                        </Box>
                    </Box>
                );
            case 'yearEngagement':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MBFYearEngagementChart data={yearEngagement} isSampleData={isYearEngagementEmpty} isFullscreen={true} />
                        </Box>
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Box sx={{ py: 2 }}>
            {/* Fullscreen Chart Modal */}
            <FullscreenChartModal
                open={Boolean(maximizedChart)}
                onClose={() => setMaximizedChart(null)}
                title={getChartTitle(maximizedChart)}
            >
                {renderChartContent(maximizedChart)}
            </FullscreenChartModal>

            {/* Row 1: Cumulative Footfall & Individual TL Footfall */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <MBFCumulativeFootfallChart
                        data={cumulativeFootfall}
                        isSampleData={isCumulativeEmpty}
                        onMaximize={() => setMaximizedChart('cumulativeFootfall')}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <MBFIndividualFootfallChart
                        data={individualTLFootfall}
                        isSampleData={isIndividualEmpty}
                        onMaximize={() => setMaximizedChart('individualFootfall')}
                    />
                </Grid>
            </Grid>

            {/* Row 2: Peak Hours Analysis & Active Users Status */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <MBFPeakHoursChart
                        data={peakHoursAnalysis}
                        isSampleData={isPeakHoursEmpty}
                        onMaximize={() => setMaximizedChart('peakHours')}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <MBFActiveUsersChart
                        data={activeUsersStatus}
                        isSampleData={isActiveUsersEmpty}
                        onMaximize={() => setMaximizedChart('activeUsers')}
                    />
                </Grid>
            </Grid>

            {/* Row 3: Department-wise Engagement & Year-wise Engagement */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <MBFDepartmentEngagementChart
                        data={departmentEngagement}
                        isSampleData={isDeptEngagementEmpty}
                        onMaximize={() => setMaximizedChart('departmentEngagement')}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <MBFYearEngagementChart
                        data={yearEngagement}
                        isSampleData={isYearEngagementEmpty}
                        onMaximize={() => setMaximizedChart('yearEngagement')}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default MBFEngagementTab;
